import React, { FunctionComponent, ReactNode, useState } from 'react';
import styled, { createGlobalStyle, css } from 'styled-components';
import moment from 'moment';
import Calendar from 'react-calendar';
import { Calendar as CalendarIcon, Check, ChevronDown, Send, Users } from 'lucide-react';
import { styles } from '../../../../../styles/themes/style';
import DropdownMenu from '../../../../../components/dropdown/DropdownMenu';
import { ICampaign, ISenderIdentity } from '../../../../../interfaces/models/campaign';
import { campaignMobile } from '../../../utils/campaignBreakpoints';

export type DatePreset = 'all' | '7d' | '4w' | '3m' | '6m' | '12m' | 'custom';

export const DATE_PRESETS: { value: DatePreset; label: string }[] = [
  { value: '7d', label: 'Last 7 days' },
  { value: '4w', label: 'Last 4 weeks' },
  { value: '3m', label: 'Last 3 months' },
  { value: '6m', label: 'Last 6 months' },
  { value: '12m', label: 'Last 12 months' },
  { value: 'custom', label: 'Custom date' },
];

export interface IDateRange {
  preset: DatePreset;
  /** YYYY-MM-DD, inclusive. Both empty for "All time". */
  from: string;
  to: string;
}

/** Everything that ever happened. The lead list opens on it; analytics offers it as the first preset. */
export const ALL_TIME_RANGE: IDateRange = { preset: 'all', from: '', to: '' };
export const LEAD_DATE_PRESETS: { value: DatePreset; label: string }[] = [
  { value: 'all', label: 'All time' },
  ...DATE_PRESETS,
];

/** Ranges end today and include it, so "Last 7 days" is today and the six before. */
export function rangeForPreset(preset: Exclude<DatePreset, 'custom' | 'all'>): IDateRange {
  const to = moment();
  const from = {
    '7d': () => to.clone().subtract(6, 'days'),
    '4w': () => to.clone().subtract(27, 'days'),
    '3m': () => to.clone().subtract(3, 'months').add(1, 'day'),
    '6m': () => to.clone().subtract(6, 'months').add(1, 'day'),
    '12m': () => to.clone().subtract(12, 'months').add(1, 'day'),
  }[preset]();
  return { preset, from: from.format('YYYY-MM-DD'), to: to.format('YYYY-MM-DD') };
}

function dateLabel(range: IDateRange): string {
  if (range.preset !== 'custom') return LEAD_DATE_PRESETS.find(p => p.value === range.preset)?.label ?? '';
  const from = moment(range.from);
  const to = moment(range.to);
  const sameYear = from.year() === to.year();
  return `${from.format(sameYear ? 'MMM D' : 'MMM D, YYYY')} – ${to.format('MMM D, YYYY')}`;
}

// ── Generic trigger + menu ───────────────────────────────────────────────────

interface IFilterDropdownProps {
  icon: ReactNode;
  label: string;
  width: number;
  open: boolean;
  onToggle: (open: boolean) => void;
  /** Multi-selects stay open while the user ticks several options. */
  stayOpen?: boolean;
  bold?: boolean;
  /** Pill-sized, to sit in a row of filter chips. */
  chip?: boolean;
  children: ReactNode;
}

const FilterDropdown: FunctionComponent<IFilterDropdownProps> = ({ icon, label, width, open, onToggle, stayOpen, bold, chip, children }) => (
  <FilterWrap $width={chip ? undefined : width} $chip={chip}>
    <StyledDropdown
      $chip={chip}
      className="performance-filter sidebar-actions-v2"
      align="start"
      $hideToggle
      hideArrow
      show={open}
      onToggle={onToggle}
      {...(stayOpen ? { autoClose: 'outside' } : {})}
      title={(
        <Trigger $bold={bold}>
          <TriggerLabel>
            {icon}
            <span>{label}</span>
          </TriggerLabel>
          <Chevron $open={open}><ChevronDown size={20} strokeWidth={1.67} /></Chevron>
        </Trigger>
      )}
    >
      <MenuPanel>{children}</MenuPanel>
    </StyledDropdown>
  </FilterWrap>
);

const MenuOption: FunctionComponent<{ selected: boolean; onClick: () => void; children: ReactNode }> = ({ selected, onClick, children }) => (
  <MenuItem onClick={onClick}>
    <MenuCheck>{selected ? <Check size={14} strokeWidth={2.5} /> : null}</MenuCheck>
    <span>{children}</span>
  </MenuItem>
);

// ── Filters ──────────────────────────────────────────────────────────────────

interface IPerformanceFiltersProps {
  range: IDateRange;
  onRangeChange: (range: IDateRange) => void;
  campaigns: ICampaign[];
  campaignIds: string[];
  onCampaignIdsChange: (ids: string[]) => void;
  identities: ISenderIdentity[];
  identityIds: string[];
  onIdentityIdsChange: (ids: string[]) => void;
  currentUserId?: string;
  /** Off on a single campaign's page, where the campaign is already fixed. */
  showCampaignFilter?: boolean;
  /** Date choices; the lead list adds "All time". */
  presets?: { value: DatePreset; label: string }[];
  /** Just the date filter, as a pill inside the caller's own row of chips. */
  chip?: boolean;
}

const PerformanceFilters: FunctionComponent<IPerformanceFiltersProps> = ({
  range, onRangeChange, campaigns, campaignIds, onCampaignIdsChange,
  identities, identityIds, onIdentityIdsChange, currentUserId, showCampaignFilter = true,
  presets = DATE_PRESETS, chip = false,
}) => {
  const [openMenu, setOpenMenu] = useState<'date' | 'campaigns' | 'senders' | null>(null);
  const [showCalendar, setShowCalendar] = useState(range.preset === 'custom');
  const toggle = (menu: 'date' | 'campaigns' | 'senders') => (open: boolean) => {
    setOpenMenu(open ? menu : null);
    if (menu === 'date' && open) setShowCalendar(range.preset === 'custom');
  };

  const senderName = (i: ISenderIdentity) => {
    const name = i.name || i.linkedin?.name || i.mail?.fromName || 'Unnamed sender';
    return i.isMe ? `${name} (Me)` : name;
  };

  const campaignLabel = campaignIds.length === 0
    ? 'All campaigns'
    : campaignIds.length === 1
      ? campaigns.find(c => c._id === campaignIds[0])?.name ?? '1 campaign'
      : `${campaignIds.length} campaigns`;

  const senderLabel = identityIds.length === 0
    ? 'All'
    : identityIds.length === 1
      ? (() => { const i = identities.find(s => String(s._id) === identityIds[0]); return i ? senderName(i) : '1 selected'; })()
      : `${identityIds.length} selected`;

  const toggleId = (list: string[], id: string) => (list.includes(id) ? list.filter(x => x !== id) : [...list, id]);

  return (
    <Toolbar $inline={chip}>
      <PerformanceFilterGlobalStyles />

      <FilterDropdown
        icon={<CalendarIcon size={20} strokeWidth={1.5} />}
        label={dateLabel(range)}
        width={196}
        bold
        chip={chip}
        open={openMenu === 'date'}
        onToggle={toggle('date')}
        stayOpen
      >
        {showCalendar ? (
          <CalendarBox>
            <StyledCalendar
              selectRange
              // "All time" has no dates to show yet.
              value={range.from ? [moment(range.from).toDate(), moment(range.to).toDate()] : null}
              maxDate={new Date()}
              showNeighboringMonth={false}
              locale="en-US"
              onChange={(value) => {
                if (!Array.isArray(value) || !value[0] || !value[1]) return;
                onRangeChange({
                  preset: 'custom',
                  from: moment(value[0] as Date).format('YYYY-MM-DD'),
                  to: moment(value[1] as Date).format('YYYY-MM-DD'),
                });
                setOpenMenu(null);
              }}
            />
            <BackLink type="button" onClick={() => setShowCalendar(false)}>Back to presets</BackLink>
          </CalendarBox>
        ) : presets.map(p => (
          <MenuOption
            key={p.value}
            selected={range.preset === p.value}
            onClick={() => {
              if (p.value === 'custom') { setShowCalendar(true); return; }
              onRangeChange(p.value === 'all' ? ALL_TIME_RANGE : rangeForPreset(p.value));
              setOpenMenu(null);
            }}
          >
            {p.label}
          </MenuOption>
        ))}
      </FilterDropdown>

      {showCampaignFilter && (
      <FilterDropdown
        icon={<Send size={20} strokeWidth={1.5} />}
        label={campaignLabel}
        width={196}
        open={openMenu === 'campaigns'}
        onToggle={toggle('campaigns')}
        stayOpen
      >
        <MenuOption selected={campaignIds.length === 0} onClick={() => onCampaignIdsChange([])}>All campaigns</MenuOption>
        {campaigns.map(c => (
          <MenuOption key={c._id} selected={campaignIds.includes(c._id)} onClick={() => onCampaignIdsChange(toggleId(campaignIds, c._id))}>
            {c.name}
          </MenuOption>
        ))}
      </FilterDropdown>
      )}

      {identities.length > 0 && (
        <FilterDropdown
          icon={<Users size={20} strokeWidth={1.5} />}
          label={`Senders: ${senderLabel}`}
          width={283}
          open={openMenu === 'senders'}
          onToggle={toggle('senders')}
          stayOpen
        >
          <MenuOption selected={identityIds.length === 0} onClick={() => onIdentityIdsChange([])}>All senders</MenuOption>
          {identities.map(i => (
            <MenuOption key={i._id} selected={identityIds.includes(String(i._id))} onClick={() => onIdentityIdsChange(toggleId(identityIds, String(i._id)))}>
              {senderName(i)}
            </MenuOption>
          ))}
        </FilterDropdown>
      )}
    </Toolbar>
  );
};

export default PerformanceFilters;

// ── Styles ───────────────────────────────────────────────────────────────────

const PerformanceFilterGlobalStyles = createGlobalStyle`
  .performance-filter .actions-dropdown + div,
  .performance-filter .dropdown-menu {
    padding: 0;
    border: 1px solid #e8eaed;
    border-radius: 10px;
    box-shadow: 0px 0px 6px rgba(51, 57, 81, 0.1);
    background: #fff;
    min-width: 200px;
    width: max-content;
    max-height: 360px !important;
    overflow-y: auto !important;
  }

  .performance-filter .dropdown-toggle::after,
  .performance-filter .dropdown-toggle::before {
    display: none !important;
    content: none !important;
  }
`;

const Toolbar = styled.div<{ $inline?: boolean }>`
  display: flex; align-items: center; gap: 12px; flex-wrap: wrap;
  padding: 0 24px;
  ${campaignMobile`padding: 0 16px; flex-direction: column; align-items: stretch;`}
  /* As a chip it joins the caller's row instead of laying out its own. */
  ${({ $inline }) => $inline && css`&& { display: contents; }`}
`;

const FilterWrap = styled.div<{ $width?: number; $chip?: boolean }>`
  width: ${({ $width }) => ($width ? `${$width}px` : 'auto')};
  flex-shrink: 0;
  ${({ $chip }) => !$chip && campaignMobile`width: 100%;`}
`;

const StyledDropdown = styled(DropdownMenu)<{ $chip?: boolean }>`
  &.dropdown {
    width: 100%;
    border: 1px solid #dae0e8;
    border-radius: 8px;
    background: ${styles.colors.white};
    transition: box-shadow 0.15s;

    .dropdown-toggle {
      height: 38px;
      width: 100%;
      padding: 0 8px 0 14px;
      border-radius: 8px;
    }

    .dropdownWrapperReference { width: 100%; min-width: 0; }

    &.show, &:hover { box-shadow: 0 0 0 3px rgba(89, 100, 255, 0.08); }
  }

  /* Same height and shape as the lead list's filter chips. */
  ${({ $chip }) => $chip && css`
    &.dropdown {
      border-color: #E5E7EB; border-radius: 100px;
      .dropdown-toggle { height: 30px; padding: 0 6px 0 10px; border-radius: 100px; }
      span { font-size: 13px; line-height: 18px; }
      svg { width: 16px; height: 16px; }
    }
  `}
`;

const Trigger = styled.div<{ $bold?: boolean }>`
  display: flex; align-items: center; justify-content: space-between; gap: 8px;
  width: 100%;
  font-family: 'Nunito Sans', NunitoSans, sans-serif;
  font-size: 14px; line-height: 20px;
  font-weight: ${({ $bold }) => ($bold ? 600 : 400)};
  color: #333951;
`;

const TriggerLabel = styled.span`
  display: inline-flex; align-items: center; gap: 8px; min-width: 0;
  svg { flex-shrink: 0; color: #8c96ad; }
  span { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
`;

const Chevron = styled.span<{ $open: boolean }>`
  display: inline-flex; flex-shrink: 0; color: #8c96ad;
  transition: transform 0.15s ease;
  transform: rotate(${({ $open }) => ($open ? 180 : 0)}deg);
`;

const MenuPanel = styled.div`
  padding: 8px;
  display: flex; flex-direction: column;
  font-family: 'Nunito Sans', sans-serif;
`;

const MenuItem = styled.div`
  display: flex; align-items: center; gap: 8px;
  padding: 6px; border-radius: 8px;
  cursor: pointer; user-select: none;
  font-size: 14px; line-height: 20px; color: #41495e;
  span { white-space: nowrap; }
  &:hover { background: #f3f4f6; }
`;

const MenuCheck = styled.span`
  width: 14px; height: 14px; flex-shrink: 0;
  display: inline-flex; align-items: center; justify-content: center;
  color: ${styles.colors.primary600};
`;

const CalendarBox = styled.div`
  padding: 4px;
  display: flex; flex-direction: column; gap: 8px;
`;

const BackLink = styled.button`
  align-self: flex-start;
  border: none; background: transparent; padding: 0 4px;
  font-family: NunitoSans; font-size: 13px; font-weight: 600;
  color: ${styles.colors.primary600}; cursor: pointer;
`;

const StyledCalendar = styled(Calendar)`
  width: 252px;
  font-family: NunitoSans; font-size: 14px; font-weight: 600;
  color: ${styles.colors.black600};

  & abbr { text-decoration: none; }

  & button {
    border: none; background: transparent;
    &:not(:disabled) { color: #333951; cursor: pointer; }
    &:disabled { color: #b8bfcf; }
  }

  & .react-calendar__navigation { display: flex; height: 24px; margin-bottom: 8px; }
  & .react-calendar__navigation__label { font-weight: 700; }
  & .react-calendar__navigation__arrow { font-size: 20px; color: #8c96ad; }
  & .react-calendar__month-view__weekdays { text-align: center; font-size: 11px; color: #8c96ad; margin-bottom: 4px; }
  & .react-calendar__tile { height: 30px; &:hover { color: #5964ff; } }
  & .react-calendar__tile--active { background: #eef3ff; }
  & .react-calendar__tile--rangeStart, & .react-calendar__tile--rangeEnd {
    border-radius: 6px; background: #5964ff; color: #fff !important;
  }
`;
